import cs from 'classnames';
import React from 'react';
import { useThrottleCallback } from '@react-hook/throttle';
import { useOnWindowResize } from '../../../../hooks/useOnWindowResize';
import { injectV2 } from '../../../../injectV2';
import { RoutingStore } from '../../../../stores/RoutingStore';
import { ExperimentsStore } from '../../../../../stores/ExperimentsStore';
import { WithTranslations, withTranslations } from '../../../../../utils/withTranslations';
import { categoryTagListDataHooks } from './CategoryTagList.dataHooks';
import { CategoryProps, CategoryTag } from './CategoryTag/CategoryTag';
import s from './CategoryTagList.scss';

interface CategoryTagListProps extends WithTranslations {
  categories: Pick<CategoryProps, 'slug' | 'title' | 'location' | 'isActive' | 'isAll'>[];
  routingStore: RoutingStore;
  experimentsStore: ExperimentsStore;
  requestUUID: string;
  dataHook?: string;
}

const CategoryTagListCmp: React.FC<CategoryTagListProps> = (props) => {
  const { categories, dataHook, requestUUID, routingStore, experimentsStore, t } = props;
  const isExpandedByDefault = experimentsStore.is('specs.funnel.TemplatesGalleryEnableFilters', 'true');

  const [lastCategoryInFirstLineIndex, setLastCategoryInFirstLineIndex] = React.useState<number>(categories.length - 1);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(typeof window === 'undefined');

  const ulRef = React.useRef<HTMLUListElement>();
  const itemRefs = React.useRef<HTMLLIElement[]>([]);
  const showMoreRef = React.useRef<HTMLDivElement>();

  const onItemClick = (isAll: boolean) => isAll && setIsExpanded(false);

  const recalcLastCategoryInFirstLine = React.useCallback(() => {
    const activeCategoryIndex = categories.findIndex((category) => category.isActive);
    const lastCategoryInFirstLineIndex = categories.findIndex((category, index) => {
      if (index === categories.length - 1) {
        return true;
      }
      const current = itemRefs.current[index];
      const next = itemRefs.current[index + 1];
      return current.offsetTop !== next.offsetTop;
    });
    if (activeCategoryIndex > lastCategoryInFirstLineIndex || lastCategoryInFirstLineIndex === categories.length - 1) {
      setIsExpanded(true);
    }
    setLastCategoryInFirstLineIndex(
      lastCategoryInFirstLineIndex === -1 ? categories.length - 1 : lastCategoryInFirstLineIndex,
    );
  }, [categories]);

  const throttleTimeout = 200;
  const { activeCategorySlug } = routingStore;
  useOnWindowResize(useThrottleCallback(recalcLastCategoryInFirstLine, 1000 / throttleTimeout));
  React.useEffect(recalcLastCategoryInFirstLine, [requestUUID, recalcLastCategoryInFirstLine]);

  return (
    <nav
      className={s.root}
      data-hook={dataHook}
      aria-label={t('categoryTagList.nav.ariaLabel', { activeCategorySlug })}
    >
      <ul className={cs(s.list, { [s.expanded]: isExpanded || isExpandedByDefault })} ref={ulRef}>
        {categories.map((category, index) => (
          <CategoryTag
            {...category}
            key={category.slug}
            onClick={() => onItemClick(category.isAll)}
            itemRef={itemRefs.current ? (ref) => (itemRefs.current[index] = ref) : undefined}
            dataHook={categoryTagListDataHooks.category({ slug: category.slug })}
            order={index}
          />
        ))}
        {!isExpanded && !isExpandedByDefault && (
          <li
            className={s.showMoreWrapper}
            onClick={() => setIsExpanded(true)}
            style={{ order: lastCategoryInFirstLineIndex }}
            data-hook={categoryTagListDataHooks.showMoreBtn()}
            aria-hidden="true"
          >
            <div className={s.showMore} ref={showMoreRef}>
              ...
            </div>
          </li>
        )}
      </ul>
    </nav>
  );
};

export const CategoryTagList = withTranslations(injectV2('routingStore', 'experimentsStore')(CategoryTagListCmp));
